/**
 * useColumnFilters — state hook cho filter per-column.
 *
 * Lưu map `{ columnKey: ColumnFilterValue }`. setFilter(null) → xoá entry.
 * Khi `value` rỗng/inactive (vd text='' hoặc selected=[]), auto-clean để
 * `hasAny` và `activeKeys` phản ánh chính xác.
 */
import { useCallback, useMemo, useState } from 'react'

import { isFilterActive, type ColumnFiltersState, type ColumnFilterValue } from './columnFilters'

export interface UseColumnFiltersResult {
  filters: ColumnFiltersState
  /** Set/clear filter cho 1 column. Truyền `null` để xoá. */
  setFilter: (columnKey: string, value: ColumnFilterValue | null) => void
  /** Xoá toàn bộ — gọi cùng với clear global Search trong "Clear All". */
  clearAll: () => void
  /** Mảng các column key đang có filter active (sorted insertion order). */
  activeKeys: string[]
  hasAny: boolean
  isActive: (columnKey: string) => boolean
}

export function useColumnFilters(): UseColumnFiltersResult {
  const [filters, setFilters] = useState<ColumnFiltersState>({})

  const setFilter = useCallback((columnKey: string, value: ColumnFilterValue | null) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (value == null || !isFilterActive(value)) {
        delete next[columnKey]
      } else {
        next[columnKey] = value
      }
      return next
    })
  }, [])

  const clearAll = useCallback(() => setFilters({}), [])

  const activeKeys = useMemo(
    () => Object.keys(filters).filter((k) => isFilterActive(filters[k])),
    [filters],
  )

  const isActive = useCallback((columnKey: string) => isFilterActive(filters[columnKey]), [filters])

  return {
    filters,
    setFilter,
    clearAll,
    activeKeys,
    hasAny: activeKeys.length > 0,
    isActive,
  }
}
